import React, { useState } from 'react';
import { 
  AlertTriangle, Search, Filter, TrendingUp, DollarSign, 
  FileText, Shield, Users, Activity, Download,
  Snowflake, Eye, XCircle
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Scatter, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import Button from '../../components/atoms/Button';
import Input from '../../components/atoms/Input';
import Select from '../../components/atoms/Select';
import StatCard from '../../components/molecules/StatCard';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { downloadProviderReport } from '../../utils/pdfGenerator';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

interface Provider {
  id: string;
  name: string;
  specialty: string;
  avgCost: number;
  patientCount: number;
  fwaScore: number;
  brandGenericRatio: number;
  upcodingRate: number;
  status: 'normal' | 'frozen' | 'under-investigation';
}

const FraudDetectionDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'metrics' | 'detection' | 'enforcement'>('metrics');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [showInvestigationModal, setShowInvestigationModal] = useState(false);

  // Mock data for providers
  const providers: Provider[] = [
    {
      id: '1',
      name: 'Dr. Ahmed Al-Mansouri',
      specialty: 'Cardiology',
      avgCost: 8500,
      patientCount: 45,
      fwaScore: 85,
      brandGenericRatio: 75,
      upcodingRate: 12,
      status: 'normal'
    },
    {
      id: '2',
      name: 'Dubai Medical Center',
      specialty: 'Multi-Specialty',
      avgCost: 12000,
      patientCount: 120,
      fwaScore: 45,
      brandGenericRatio: 35,
      upcodingRate: 5,
      status: 'normal'
    },
    {
      id: '3',
      name: 'Dr. Sarah Johnson',
      specialty: 'Orthopedics',
      avgCost: 15000,
      patientCount: 25,
      fwaScore: 92,
      brandGenericRatio: 85,
      upcodingRate: 25,
      status: 'under-investigation'
    },
    {
      id: '4',
      name: 'City Health Clinic',
      specialty: 'General Practice',
      avgCost: 6500,
      patientCount: 200,
      fwaScore: 35,
      brandGenericRatio: 30,
      upcodingRate: 3,
      status: 'normal'
    },
    {
      id: '5',
      name: 'Dr. Mohammed Hassan',
      specialty: 'Dermatology',
      avgCost: 18000,
      patientCount: 15,
      fwaScore: 95,
      brandGenericRatio: 90,
      upcodingRate: 30,
      status: 'frozen'
    },
    {
      id: '6',
      name: 'Al Zahra Hospital Dubai',
      specialty: 'Multi-Specialty',
      avgCost: 11500,
      patientCount: 180,
      fwaScore: 42,
      brandGenericRatio: 38,
      upcodingRate: 6,
      status: 'normal'
    },
    {
      id: '7',
      name: 'Dr. Fatima Al-Suwaidi',
      specialty: 'Pediatrics',
      avgCost: 5200,
      patientCount: 95,
      fwaScore: 28,
      brandGenericRatio: 25,
      upcodingRate: 2,
      status: 'normal'
    },
    {
      id: '8',
      name: 'Emirates Hospital',
      specialty: 'Multi-Specialty',
      avgCost: 13500,
      patientCount: 250,
      fwaScore: 55,
      brandGenericRatio: 48,
      upcodingRate: 8,
      status: 'normal'
    },
    {
      id: '9',
      name: 'Dr. Khalid Al-Mazrouei',
      specialty: 'Neurology',
      avgCost: 16500,
      patientCount: 32,
      fwaScore: 88,
      brandGenericRatio: 82,
      upcodingRate: 22,
      status: 'under-investigation'
    },
    {
      id: '10',
      name: 'Abu Dhabi Clinic',
      specialty: 'General Practice',
      avgCost: 4800,
      patientCount: 310,
      fwaScore: 31,
      brandGenericRatio: 28,
      upcodingRate: 4,
      status: 'normal'
    },
    {
      id: '11',
      name: 'Dr. Aisha Mohammed',
      specialty: 'Obstetrics',
      avgCost: 7200,
      patientCount: 68,
      fwaScore: 38,
      brandGenericRatio: 32,
      upcodingRate: 5,
      status: 'normal'
    },
    {
      id: '12',
      name: 'Prime Medical Center',
      specialty: 'Multi-Specialty',
      avgCost: 9800,
      patientCount: 145,
      fwaScore: 52,
      brandGenericRatio: 44,
      upcodingRate: 9,
      status: 'normal'
    },
    {
      id: '13',
      name: 'Dr. Omar Al-Kaabi',
      specialty: 'Cardiology',
      avgCost: 19000,
      patientCount: 28,
      fwaScore: 94,
      brandGenericRatio: 88,
      upcodingRate: 28,
      status: 'frozen'
    },
    {
      id: '14',
      name: 'Sharjah Family Clinic',
      specialty: 'General Practice',
      avgCost: 5500,
      patientCount: 220,
      fwaScore: 33,
      brandGenericRatio: 29,
      upcodingRate: 3,
      status: 'normal'
    },
    {
      id: '15',
      name: 'Dr. Maryam Al-Shamsi',
      specialty: 'Dermatology',
      avgCost: 8900,
      patientCount: 52,
      fwaScore: 46,
      brandGenericRatio: 41,
      upcodingRate: 7,
      status: 'normal'
    },
    {
      id: '16',
      name: 'Al Ain Medical Complex',
      specialty: 'Multi-Specialty',
      avgCost: 10200,
      patientCount: 165,
      fwaScore: 48,
      brandGenericRatio: 39,
      upcodingRate: 6,
      status: 'normal'
    },
    {
      id: '17',
      name: 'Dr. Rashid Al-Blooshi',
      specialty: 'Orthopedics',
      avgCost: 17500,
      patientCount: 38,
      fwaScore: 86,
      brandGenericRatio: 79,
      upcodingRate: 18,
      status: 'under-investigation'
    },
    {
      id: '18',
      name: 'Mediclinic Dubai',
      specialty: 'Multi-Specialty',
      avgCost: 11800,
      patientCount: 195,
      fwaScore: 44,
      brandGenericRatio: 36,
      upcodingRate: 5,
      status: 'normal'
    },
    {
      id: '19',
      name: 'Dr. Noura Al-Ketbi',
      specialty: 'Pediatrics',
      avgCost: 6100,
      patientCount: 112,
      fwaScore: 29,
      brandGenericRatio: 26,
      upcodingRate: 3,
      status: 'normal'
    },
    {
      id: '20',
      name: 'Gulf Diagnostic Center',
      specialty: 'Radiology',
      avgCost: 7800,
      patientCount: 285,
      fwaScore: 58,
      brandGenericRatio: 52,
      upcodingRate: 11,
      status: 'normal'
    },
    {
      id: '21',
      name: 'Dr. Salem Al-Dhaheri',
      specialty: 'Neurology',
      avgCost: 20000,
      patientCount: 22,
      fwaScore: 97,
      brandGenericRatio: 93,
      upcodingRate: 32,
      status: 'frozen'
    },
    {
      id: '22',
      name: 'Aster Hospital',
      specialty: 'Multi-Specialty',
      avgCost: 10500,
      patientCount: 175,
      fwaScore: 41,
      brandGenericRatio: 34,
      upcodingRate: 4,
      status: 'normal'
    },
    {
      id: '23',
      name: 'Dr. Hessa Al-Mazrouei',
      specialty: 'Obstetrics',
      avgCost: 8200,
      patientCount: 74,
      fwaScore: 39,
      brandGenericRatio: 33,
      upcodingRate: 5,
      status: 'normal'
    },
    {
      id: '24',
      name: 'NMC Hospital',
      specialty: 'Multi-Specialty',
      avgCost: 12500,
      patientCount: 210,
      fwaScore: 49,
      brandGenericRatio: 42,
      upcodingRate: 7,
      status: 'normal'
    },
    {
      id: '25',
      name: 'Dr. Abdullah Al-Suwaidi',
      specialty: 'Cardiology',
      avgCost: 14500,
      patientCount: 48,
      fwaScore: 82,
      brandGenericRatio: 74,
      upcodingRate: 16,
      status: 'under-investigation'
    },
    {
      id: '26',
      name: 'Ajman Medical Center',
      specialty: 'General Practice',
      avgCost: 5900,
      patientCount: 188,
      fwaScore: 36,
      brandGenericRatio: 31,
      upcodingRate: 4,
      status: 'normal'
    },
    {
      id: '27',
      name: 'Dr. Latifa Al-Hashimi',
      specialty: 'Dermatology',
      avgCost: 9500,
      patientCount: 61,
      fwaScore: 51,
      brandGenericRatio: 45,
      upcodingRate: 8,
      status: 'normal'
    },
    {
      id: '28',
      name: 'Saudi German Hospital',
      specialty: 'Multi-Specialty',
      avgCost: 13200,
      patientCount: 225,
      fwaScore: 47,
      brandGenericRatio: 40,
      upcodingRate: 6,
      status: 'normal'
    },
    {
      id: '29',
      name: 'Dr. Hamad Al-Nahyan',
      specialty: 'Orthopedics',
      avgCost: 18500,
      patientCount: 29,
      fwaScore: 91,
      brandGenericRatio: 86,
      upcodingRate: 24,
      status: 'frozen'
    },
    {
      id: '30',
      name: 'Fujairah Hospital',
      specialty: 'Multi-Specialty',
      avgCost: 9200,
      patientCount: 142,
      fwaScore: 43,
      brandGenericRatio: 37,
      upcodingRate: 5,
      status: 'normal'
    },
    {
      id: '31',
      name: 'Dr. Shamma Al-Falasi',
      specialty: 'Pediatrics',
      avgCost: 5800,
      patientCount: 98,
      fwaScore: 32,
      brandGenericRatio: 27,
      upcodingRate: 3,
      status: 'normal'
    },
    {
      id: '32',
      name: 'American Hospital Dubai',
      specialty: 'Multi-Specialty',
      avgCost: 14800,
      patientCount: 268,
      fwaScore: 53,
      brandGenericRatio: 46,
      upcodingRate: 9,
      status: 'normal'
    },
    {
      id: '33',
      name: 'Dr. Majid Al-Qassimi',
      specialty: 'Neurology',
      avgCost: 17800,
      patientCount: 35,
      fwaScore: 87,
      brandGenericRatio: 81,
      upcodingRate: 20,
      status: 'under-investigation'
    },
    {
      id: '34',
      name: 'RAK Hospital',
      specialty: 'Multi-Specialty',
      avgCost: 8900,
      patientCount: 156,
      fwaScore: 40,
      brandGenericRatio: 35,
      upcodingRate: 5,
      status: 'normal'
    },
    {
      id: '35',
      name: 'Dr. Maitha Al-Mansoori',
      specialty: 'Obstetrics',
      avgCost: 7600,
      patientCount: 82,
      fwaScore: 37,
      brandGenericRatio: 31,
      upcodingRate: 4,
      status: 'normal'
    },
    {
      id: '36',
      name: 'Burjeel Hospital',
      specialty: 'Multi-Specialty',
      avgCost: 11200,
      patientCount: 192,
      fwaScore: 45,
      brandGenericRatio: 38,
      upcodingRate: 6,
      status: 'normal'
    },
    {
      id: '37',
      name: 'Dr. Jassim Al-Ali',
      specialty: 'Cardiology',
      avgCost: 16200,
      patientCount: 41,
      fwaScore: 84,
      brandGenericRatio: 77,
      upcodingRate: 17,
      status: 'under-investigation'
    },
    {
      id: '38',
      name: 'Thumbay Hospital',
      specialty: 'Multi-Specialty',
      avgCost: 7200,
      patientCount: 205,
      fwaScore: 38,
      brandGenericRatio: 32,
      upcodingRate: 4,
      status: 'normal'
    },
    {
      id: '39',
      name: 'Dr. Sara Al-Balooshi',
      specialty: 'Dermatology',
      avgCost: 10200,
      patientCount: 56,
      fwaScore: 54,
      brandGenericRatio: 48,
      upcodingRate: 10,
      status: 'normal'
    },
    {
      id: '40',
      name: 'Zulekha Hospital',
      specialty: 'Multi-Specialty',
      avgCost: 9600,
      patientCount: 178,
      fwaScore: 42,
      brandGenericRatio: 36,
      upcodingRate: 5,
      status: 'normal'
    },
    {
      id: '41',
      name: 'Dr. Yousef Al-Shamsi',
      specialty: 'Orthopedics',
      avgCost: 19500,
      patientCount: 26,
      fwaScore: 93,
      brandGenericRatio: 89,
      upcodingRate: 27,
      status: 'frozen'
    },
    {
      id: '42',
      name: 'Lifecare Hospital',
      specialty: 'Multi-Specialty',
      avgCost: 8500,
      patientCount: 168,
      fwaScore: 44,
      brandGenericRatio: 37,
      upcodingRate: 6,
      status: 'normal'
    },
    {
      id: '43',
      name: 'Dr. Amna Al-Mazrouei',
      specialty: 'Pediatrics',
      avgCost: 6400,
      patientCount: 105,
      fwaScore: 30,
      brandGenericRatio: 26,
      upcodingRate: 3,
      status: 'normal'
    },
    {
      id: '44',
      name: 'Rashid Hospital',
      specialty: 'Multi-Specialty',
      avgCost: 10800,
      patientCount: 232,
      fwaScore: 46,
      brandGenericRatio: 39,
      upcodingRate: 7,
      status: 'normal'
    },
    {
      id: '45',
      name: 'Dr. Nasser Al-Ketbi',
      specialty: 'Neurology',
      avgCost: 18200,
      patientCount: 31,
      fwaScore: 89,
      brandGenericRatio: 83,
      upcodingRate: 21,
      status: 'under-investigation'
    },
    {
      id: '46',
      name: 'Iranian Hospital',
      specialty: 'Multi-Specialty',
      avgCost: 7800,
      patientCount: 198,
      fwaScore: 39,
      brandGenericRatio: 33,
      upcodingRate: 4,
      status: 'normal'
    },
    {
      id: '47',
      name: 'Dr. Moza Al-Suwaidi',
      specialty: 'Obstetrics',
      avgCost: 8600,
      patientCount: 77,
      fwaScore: 41,
      brandGenericRatio: 35,
      upcodingRate: 5,
      status: 'normal'
    },
    {
      id: '48',
      name: 'Medcare Hospital',
      specialty: 'Multi-Specialty',
      avgCost: 12800,
      patientCount: 215,
      fwaScore: 50,
      brandGenericRatio: 43,
      upcodingRate: 8,
      status: 'normal'
    },
    {
      id: '49',
      name: 'Dr. Saeed Al-Dhaheri',
      specialty: 'Cardiology',
      avgCost: 15800,
      patientCount: 44,
      fwaScore: 83,
      brandGenericRatio: 76,
      upcodingRate: 15,
      status: 'under-investigation'
    },
    {
      id: '50',
      name: 'Al Qassimi Hospital',
      specialty: 'Multi-Specialty',
      avgCost: 6800,
      patientCount: 185,
      fwaScore: 37,
      brandGenericRatio: 32,
      upcodingRate: 4,
      status: 'normal'
    }
  ];

  // Scatter plot data for cost-per-patient comparison
  const scatterData = {
    datasets: [
      {
        label: 'Providers',
        data: providers.map(p => ({ x: p.avgCost, y: p.patientCount })),
        backgroundColor: providers.map(p => 
          p.fwaScore > 80 ? 'rgba(239, 68, 68, 0.6)' :
          p.fwaScore > 60 ? 'rgba(251, 191, 36, 0.6)' :
          'rgba(34, 197, 94, 0.6)'
        ),
        borderColor: providers.map(p => 
          p.fwaScore > 80 ? 'rgba(239, 68, 68, 1)' :
          p.fwaScore > 60 ? 'rgba(251, 191, 36, 1)' :
          'rgba(34, 197, 94, 1)'
        ),
        borderWidth: 2,
        pointRadius: 8,
        pointHoverRadius: 10,
      }
    ]
  };

  const scatterOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          label: function(context: any) {
            const provider = providers[context.dataIndex];
            return [
              `${provider.name}`,
              `Avg Cost: AED ${provider.avgCost}`,
              `Patients: ${provider.patientCount}`,
              `FWA Score: ${provider.fwaScore}`
            ];
          }
        }
      }
    },
    scales: {
      x: {
        title: { display: true, text: 'Average Claim Cost (AED)' },
        grid: { color: '#F3F4F6' }
      },
      y: {
        title: { display: true, text: 'Number of Patients' },
        grid: { color: '#F3F4F6' }
      }
    }
  };

  // Medication surveillance line chart
  const medicationData = {
    labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5', 'Week 6'],
    datasets: [
      {
        label: 'Medication A (High Cost)',
        data: [45, 52, 68, 85, 102, 125],
        borderColor: 'rgba(239, 68, 68, 1)',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        borderWidth: 3,
        tension: 0.4,
        fill: true
      },
      {
        label: 'Medication B (Generic)',
        data: [120, 115, 110, 108, 105, 100],
        borderColor: 'rgba(34, 197, 94, 1)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        borderWidth: 3,
        tension: 0.4,
        fill: true
      }
    ]
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true, position: 'top' as const },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        cornerRadius: 8,
      }
    },
    scales: {
      x: { grid: { display: false } },
      y: { 
        title: { display: true, text: 'Prescription Rate' },
        grid: { color: '#F3F4F6' }
      }
    }
  };

  const handleFreezePayment = (providerName: string) => {
    if (window.confirm(`⚠️ FREEZE PAYMENT\n\nAre you sure you want to freeze all future payments to ${providerName}?\n\nThis action will:\n- Immediately stop all pending payments\n- Require investigation report\n- Be logged in audit trail\n\nPlease confirm to proceed.`)) {
      toast.success(`Payments frozen for ${providerName}`);
      // Implement actual freeze logic
    }
  };

  const handleLaunchInvestigation = (provider: Provider) => {
    setSelectedProvider(provider);
    setShowInvestigationModal(true);
    toast.success(`Investigation launched for ${provider.name}`);
  };

  const handleViewProviderProfile = (provider: Provider) => {
    setSelectedProvider(provider);
    setShowProviderModal(true);
  };

  const renderMetricsTab = () => (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCard
          title="High Risk Providers"
          value={providers.filter(p => p.fwaScore > 80).length}
          icon={AlertTriangle}
          iconBgColor="bg-red-100"
          iconColor="text-red-600"
          subtitle="FWA Score > 80"
        />
        <StatCard
          title="Avg Brand Generic Ratio"
          value="54%"
          icon={Activity}
          iconBgColor="bg-yellow-100"
          iconColor="text-yellow-600"
          subtitle="vs 35% target"
        />
        <StatCard
          title="Upcoding Rate"
          value="8.5%"
          icon={TrendingUp}
          iconBgColor="bg-orange-100"
          iconColor="text-orange-600"
          subtitle="Industry avg: 5%"
        />
        <StatCard
          title="Saved This Month"
          value="AED 245K"
          icon={DollarSign}
          iconBgColor="bg-green-100"
          iconColor="text-green-600"
          trend={{ value: 12, isPositive: true, label: 'vs last month' }}
        />
      </div>

      {/* Cost-per-Patient Comparison */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Cost-per-Patient Comparison</h3>
            <p className="text-sm text-gray-600 mt-1">
              Identify providers with high cost on small patient volumes
            </p>
          </div>
          <Select
            options={[
              { value: '', label: 'All Specialties' },
              { value: 'cardiology', label: 'Cardiology' },
              { value: 'orthopedics', label: 'Orthopedics' },
              { value: 'dermatology', label: 'Dermatology' }
            ]}
            value={selectedSpecialty}
            onChange={setSelectedSpecialty}
            placeholder="Filter specialty"
          />
        </div>
        
        <div className="h-96">
          <Scatter data={scatterData} options={scatterOptions} />
        </div>

        <div className="mt-4 flex items-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <span className="text-gray-600">Low Risk (FWA &lt; 60)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
            <span className="text-gray-600">Medium Risk (FWA 60-80)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
            <span className="text-gray-600">High Risk (FWA &gt; 80)</span>
          </div>
        </div>
      </div>

      {/* Brand Name Medication Leaderboard */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6">
          Brand vs Generic Prescription Rate - Top 10
        </h3>
        
        <div className="space-y-4">
          {providers
            .sort((a, b) => b.brandGenericRatio - a.brandGenericRatio)
            .slice(0, 10)
            .map((provider, index) => (
              <div key={provider.id} className="flex items-center gap-4">
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center font-bold text-gray-600">
                  {index + 1}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-semibold text-gray-900">{provider.name}</p>
                      <p className="text-sm text-gray-500">{provider.specialty}</p>
                    </div>
                    <span className={`font-bold text-lg ${
                      provider.brandGenericRatio > 50 ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {provider.brandGenericRatio}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                    <div
                      className={`h-full rounded-full ${
                        provider.brandGenericRatio > 50 ? 'bg-red-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${provider.brandGenericRatio}%` }}
                    />
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleViewProviderProfile(provider)}
                >
                  View Profile
                </Button>
              </div>
            ))}
        </div>
      </div>

      {/* Upcoding Rate Scorecard */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {providers
          .filter(p => p.upcodingRate > 10)
          .map((provider) => (
            <div key={provider.id} className="bg-white rounded-2xl shadow-lg border border-orange-200 p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-orange-600" />
                </div>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">
                  High Upcoding
                </span>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">{provider.name}</h4>
              <p className="text-sm text-gray-600 mb-4">{provider.specialty}</p>
              
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Upcoding Rate:</span>
                  <span className="font-bold text-orange-600">{provider.upcodingRate}%</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Avg Industry:</span>
                  <span className="font-semibold text-gray-900">5%</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">FWA Score:</span>
                  <span className="font-bold text-red-600">{provider.fwaScore}</span>
                </div>
              </div>

              <Button 
                variant="outline" 
                size="sm" 
                className="w-full mt-4"
                onClick={() => handleViewProviderProfile(provider)}
              >
                View Details
              </Button>
            </div>
          ))}
      </div>
    </div>
  );

  const renderDetectionTab = () => (
    <div className="space-y-6">
      {/* High-Risk Fraud Alerts */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <Shield className="w-6 h-6 text-red-600" />
          High-Risk Fraud Alerts
        </h3>

        <div className="space-y-4">
          {providers
            .filter(p => p.fwaScore > 80)
            .map((provider) => (
              <div key={provider.id} className="border-2 border-red-200 rounded-xl p-4 bg-red-50">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                        {provider.fwaScore > 90 ? 'FRAUD: Services Not Provided' : 'ABUSE: Unnecessary Procedures'}
                      </span>
                      <span className="font-bold text-gray-900">{provider.name}</span>
                    </div>
                    <p className="text-sm text-gray-700 mb-2">
                      FWA Score: <strong>{provider.fwaScore}</strong> | 
                      Avg Cost: <strong>AED {provider.avgCost.toLocaleString()}</strong> | 
                      Patients: <strong>{provider.patientCount}</strong>
                    </p>
                    <div className="flex items-center gap-3 text-sm text-gray-600">
                      <span>Brand/Generic: {provider.brandGenericRatio}%</span>
                      <span>Upcoding: {provider.upcodingRate}%</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button
                      variant="danger"
                      size="sm"
                      icon={Snowflake}
                      onClick={() => handleFreezePayment(provider.name)}
                    >
                      Freeze Payment
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      icon={FileText}
                      onClick={() => handleLaunchInvestigation(provider)}
                    >
                      Launch Investigation
                    </Button>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Stopped and Recovered Amounts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl border-2 border-green-300 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-16 h-16 bg-green-500 rounded-2xl flex items-center justify-center">
              <DollarSign className="w-8 h-8 text-white" />
            </div>
            <span className="text-xs font-semibold text-green-700 bg-green-200 px-3 py-1 rounded-full">
              This Month
            </span>
          </div>
          <h4 className="text-sm font-semibold text-green-800 mb-2">Stopped Claims Value</h4>
          <p className="text-4xl font-bold text-green-900">AED 245,000</p>
          <p className="text-sm text-green-700 mt-2">↑ 12% vs last month</p>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border-2 border-blue-300 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-16 h-16 bg-blue-500 rounded-2xl flex items-center justify-center">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <span className="text-xs font-semibold text-blue-700 bg-blue-200 px-3 py-1 rounded-full">
              Active
            </span>
          </div>
          <h4 className="text-sm font-semibold text-blue-800 mb-2">Frozen Payments</h4>
          <p className="text-4xl font-bold text-blue-900">AED 87,500</p>
          <p className="text-sm text-blue-700 mt-2">5 providers under investigation</p>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl border-2 border-purple-300 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-16 h-16 bg-purple-500 rounded-2xl flex items-center justify-center">
              <TrendingUp className="w-8 h-8 text-white" />
            </div>
            <span className="text-xs font-semibold text-purple-700 bg-purple-200 px-3 py-1 rounded-full">
              YTD
            </span>
          </div>
          <h4 className="text-sm font-semibold text-purple-800 mb-2">Total Savings (2025)</h4>
          <p className="text-4xl font-bold text-purple-900">AED 1.8M</p>
          <p className="text-sm text-purple-700 mt-2">15% of total claims reviewed</p>
        </div>
      </div>

      {/* Correlation Network Analysis Placeholder */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">
          Correlation and Network Analysis
        </h3>
        <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-12 text-center border-2 border-dashed border-gray-300">
          <div className="w-20 h-20 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Users className="w-10 h-10 text-blue-600" />
          </div>
          <h4 className="text-lg font-bold text-gray-900 mb-2">Interactive Network Graph</h4>
          <p className="text-gray-600 max-w-2xl mx-auto mb-6">
            Visual display linking doctors, pharmacies, and patients to identify suspicious patterns and organized fraud rings.
            Shows relationships like shared addresses, repeated prescriptions, and clustered diagnoses.
          </p>
          <Button icon={Activity}>Launch Network Visualization</Button>
        </div>
      </div>
    </div>
  );

  const renderEnforcementTab = () => (
    <div className="space-y-6">
      {/* Provider Action Status - Search */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6">Provider Action Status</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Input
            placeholder="Search provider..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={Search}
          />
          <Select
            options={[
              { value: '', label: 'All Statuses' },
              { value: 'normal', label: 'Normal' },
              { value: 'frozen', label: 'Payment Frozen' },
              { value: 'under-investigation', label: 'Under Investigation' }
            ]}
            placeholder="Filter by status"
          />
          <Button variant="outline" icon={Filter}>Advanced Filters</Button>
        </div>

        {/* Provider Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Provider</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">FWA Score</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Claims Rejected</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Payment Status</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {providers.map((provider) => (
                <tr key={provider.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-4 px-4">
                    <p className="font-semibold text-gray-900">{provider.name}</p>
                    <p className="text-sm text-gray-500">{provider.specialty}</p>
                  </td>
                  <td className="py-4 px-4">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                      provider.fwaScore > 80 ? 'bg-red-100 text-red-700' :
                      provider.fwaScore > 60 ? 'bg-yellow-100 text-yellow-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {provider.fwaScore}
                    </span>
                  </td>
                  <td className="py-4 px-4 text-gray-700">
                    {Math.floor(provider.fwaScore / 10)}
                  </td>
                  <td className="py-4 px-4">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                      provider.status === 'frozen' ? 'bg-red-100 text-red-700' :
                      provider.status === 'under-investigation' ? 'bg-orange-100 text-orange-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {provider.status.replace('-', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2">
                      <button className="p-2 hover:bg-blue-100 rounded-lg transition-colors">
                        <Eye className="w-4 h-4 text-blue-600" />
                      </button>
                      <button 
                        onClick={() => handleFreezePayment(provider.name)}
                        className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                      >
                        <XCircle className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Investigation Queue */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6">Investigation Queue</h3>
        
        <div className="space-y-4">
          {providers
            .filter(p => p.status === 'under-investigation')
            .map((provider, index) => (
              <div key={provider.id} className="border border-orange-200 rounded-xl p-4 bg-orange-50">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="inline-flex items-center px-2 py-1 rounded bg-orange-200 text-orange-900 text-xs font-bold">
                        Case #{1000 + index}
                      </span>
                      <span className="font-bold text-gray-900">{provider.name}</span>
                    </div>
                    <p className="text-sm text-gray-700 mb-2">
                      Suspected Activity: Multiple high-value claims with similar patterns
                    </p>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span>Amount at Risk: <strong>AED {(provider.avgCost * provider.patientCount).toLocaleString()}</strong></span>
                      <span>Started: {dayjs().subtract(3, 'day').format('MMM DD, YYYY')}</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      icon={FileText}
                      onClick={() => handleViewProviderProfile(provider)}
                    >
                      View Documents
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      icon={Download}
                      onClick={() => {
                        downloadProviderReport(provider.name);
                        toast.success(`Investigation report for ${provider.name} downloaded`);
                      }}
                    >
                      Export PDF
                    </Button>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Medications Under Surveillance */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6">
          Medications Under Surveillance
        </h3>
        <p className="text-sm text-gray-600 mb-6">
          Tracking high-cost medications with unjustified prescription rate increases
        </p>
        
        <div className="h-80">
          <Line data={medicationData} options={lineOptions} />
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-sm font-semibold text-red-900 mb-1">Medication A (Brand Name)</p>
            <p className="text-xs text-red-700 mb-2">High-cost cholesterol medication</p>
            <div className="flex items-center justify-between">
              <span className="text-red-900 font-bold text-lg">↑ 178%</span>
              <span className="text-xs text-red-700">vs 6 weeks ago</span>
            </div>
          </div>

          <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
            <p className="text-sm font-semibold text-green-900 mb-1">Medication B (Generic)</p>
            <p className="text-xs text-green-700 mb-2">Generic alternative (same efficacy)</p>
            <div className="flex items-center justify-between">
              <span className="text-green-900 font-bold text-lg">↓ 17%</span>
              <span className="text-xs text-green-700">vs 6 weeks ago</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <DashboardLayout>
      <div className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Fraud, Waste & Abuse Detection</h1>
            <p className="text-lg text-gray-600">
              {dayjs().format('dddd, MMMM D, YYYY')} • Provider behavioral analysis and enforcement
            </p>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 mb-6 px-2">
            <div className="flex border-b border-gray-200 overflow-x-auto">
              <button
                onClick={() => setActiveTab('metrics')}
                className={`px-6 py-4 font-semibold text-sm whitespace-nowrap ${
                  activeTab === 'metrics'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Activity className="w-4 h-4" />
                  Behavioral Efficiency Metrics
                </div>
              </button>
              <button
                onClick={() => setActiveTab('detection')}
                className={`px-6 py-4 font-semibold text-sm whitespace-nowrap ${
                  activeTab === 'detection'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Advanced Fraud Network Detection
                </div>
              </button>
              <button
                onClick={() => setActiveTab('enforcement')}
                className={`px-6 py-4 font-semibold text-sm whitespace-nowrap ${
                  activeTab === 'enforcement'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Network Enforcement Actions
                </div>
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="mt-6">
            {activeTab === 'metrics' && renderMetricsTab()}
            {activeTab === 'detection' && renderDetectionTab()}
            {activeTab === 'enforcement' && renderEnforcementTab()}
          </div>
        </div>
      </div>

      {/* Provider Profile Modal */}
      {showProviderModal && selectedProvider && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#0077B6]/10 rounded-xl flex items-center justify-center">
                  <Shield className="w-6 h-6 text-[#0077B6]" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Provider Profile</h3>
                  <p className="text-sm text-gray-600">{selectedProvider.name}</p>
                </div>
              </div>
              <button
                onClick={() => setShowProviderModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
            </div>

            {/* Provider Status Banner */}
            <div className={`rounded-xl p-6 mb-6 border-2 ${
              selectedProvider.fwaScore > 80 
                ? 'bg-red-50 border-red-200' 
                : selectedProvider.fwaScore > 60
                ? 'bg-yellow-50 border-yellow-200'
                : 'bg-green-50 border-green-200'
            }`}>
              <div className="flex items-center gap-3 mb-4">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border ${
                  selectedProvider.status === 'frozen' ? 'bg-red-100 text-red-700 border-red-200' :
                  selectedProvider.status === 'under-investigation' ? 'bg-orange-100 text-orange-700 border-orange-200' :
                  'bg-green-100 text-green-700 border-green-200'
                }`}>
                  {selectedProvider.status.replace('-', ' ').toUpperCase()}
                </span>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${
                  selectedProvider.fwaScore > 80 ? 'bg-red-100 text-red-700' :
                  selectedProvider.fwaScore > 60 ? 'bg-yellow-100 text-yellow-700' :
                  'bg-green-100 text-green-700'
                }`}>
                  FWA SCORE: {selectedProvider.fwaScore}
                </span>
              </div>

              <div className="grid grid-cols-3 gap-6">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Specialty</p>
                  <p className="text-lg font-semibold text-gray-900">{selectedProvider.specialty}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Patients</p>
                  <p className="text-lg font-semibold text-gray-900">{selectedProvider.patientCount}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Average Cost</p>
                  <p className="text-lg font-semibold text-[#03045E]">AED {selectedProvider.avgCost.toLocaleString()}</p>
                </div>
              </div>
            </div>

            {/* Risk Indicators */}
            <div className="bg-gray-50 rounded-xl p-6 mb-6">
              <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
                Risk Indicators
              </h4>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-gray-600">FWA Risk Score</span>
                    <span className="font-bold text-gray-900">{selectedProvider.fwaScore}/100</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className={`h-3 rounded-full ${
                        selectedProvider.fwaScore > 80 ? 'bg-red-600' :
                        selectedProvider.fwaScore > 60 ? 'bg-yellow-600' :
                        'bg-green-600'
                      }`}
                      style={{ width: `${selectedProvider.fwaScore}%` }}
                    />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-gray-600">Brand/Generic Ratio</span>
                    <span className="font-bold text-gray-900">{selectedProvider.brandGenericRatio}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className={`h-3 rounded-full ${
                        selectedProvider.brandGenericRatio > 50 ? 'bg-red-600' : 'bg-green-600'
                      }`}
                      style={{ width: `${selectedProvider.brandGenericRatio}%` }}
                    />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-gray-600">Upcoding Rate</span>
                    <span className="font-bold text-gray-900">{selectedProvider.upcodingRate}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className={`h-3 rounded-full ${
                        selectedProvider.upcodingRate > 10 ? 'bg-red-600' :
                        selectedProvider.upcodingRate > 5 ? 'bg-yellow-600' :
                        'bg-green-600'
                      }`}
                      style={{ width: `${selectedProvider.upcodingRate * 3}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Financial Summary */}
            <div className="bg-[#0077B6]/5 rounded-xl p-6 mb-6">
              <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-[#0077B6]" />
                Financial Summary
              </h4>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Claims Value</p>
                  <p className="text-2xl font-bold text-[#03045E]">
                    AED {(selectedProvider.avgCost * selectedProvider.patientCount).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Estimated Waste/Abuse</p>
                  <p className="text-2xl font-bold text-red-600">
                    AED {Math.floor((selectedProvider.avgCost * selectedProvider.patientCount * selectedProvider.fwaScore) / 100).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setShowProviderModal(false)} 
                className="flex-1"
              >
                Close
              </Button>
              <Button 
                onClick={() => {
                  downloadProviderReport(selectedProvider.name);
                  toast.success(`Provider report for ${selectedProvider.name} downloaded`);
                }}
                icon={Download}
                className="flex-1 bg-[#0077B6] hover:bg-[#03045E]"
              >
                Export Report
              </Button>
              {selectedProvider.status !== 'frozen' && (
                <Button 
                  variant="danger"
                  onClick={() => {
                    handleFreezePayment(selectedProvider.name);
                    setShowProviderModal(false);
                  }}
                  icon={Snowflake}
                  className="flex-1"
                >
                  Freeze Payment
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Investigation Launch Modal */}
      {showInvestigationModal && selectedProvider && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Launch Investigation</h3>
                  <p className="text-sm text-gray-600">{selectedProvider.name}</p>
                </div>
              </div>
              <button
                onClick={() => setShowInvestigationModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-xl p-6 mb-6">
              <h4 className="font-bold text-gray-900 mb-4">Investigation Details</h4>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Provider:</span>
                  <span className="font-semibold text-gray-900">{selectedProvider.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">FWA Score:</span>
                  <span className="font-semibold text-red-600">{selectedProvider.fwaScore}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Amount at Risk:</span>
                  <span className="font-semibold text-gray-900">
                    AED {(selectedProvider.avgCost * selectedProvider.patientCount).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Investigation Start Date:</span>
                  <span className="font-semibold text-gray-900">{dayjs().format('MMM DD, YYYY')}</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 mb-6">
              <h4 className="font-bold text-gray-900 mb-3">Investigation Actions</h4>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-[#0077B6] mt-1">•</span>
                  <span>All future claims will be flagged for manual review</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#0077B6] mt-1">•</span>
                  <span>Historical claims will be audited for patterns</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#0077B6] mt-1">•</span>
                  <span>Provider will be notified of investigation</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#0077B6] mt-1">•</span>
                  <span>Investigation case file will be created</span>
                </li>
              </ul>
            </div>

            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setShowInvestigationModal(false)} 
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={() => {
                  toast.success(`Investigation case created for ${selectedProvider.name}`);
                  setShowInvestigationModal(false);
                }}
                icon={FileText}
                className="flex-1 bg-orange-600 hover:bg-orange-700"
              >
                Confirm & Launch
              </Button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default FraudDetectionDashboard;
